import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ClosingGroupCards from '../components/closing/ClosingGroupCards';
import ClosingPadPlaidButton from '../components/closing/ClosingPadPlaidButton';
import ClosingPersonSteps from '../components/closing/ClosingPersonSteps';
import {
  applyLeaseSigningStatus,
  emptyGroupState,
  groupStepProgress,
  emptyPersonal,
  membersFromGroup,
  personalOrEmpty,
  sampleGroup,
  samplePersonal,
  withPreviewRoster,
  type ClosingChecklist,
  type ClosingGroupState,
  type ClosingPersonalState,
} from '../components/closing/closing-model';
import { CLOSING_CARD } from '../components/closing/closing-styles';
import { ProgressTrack } from '../components/tenant/ProgressTrack';
import { useAutosave } from '../hooks/useAutosave';
import { useCardSaveBadges } from '../hooks/useCardSaveBadges';
import { GroupTasksMarker, ProspectPersonSwitcher } from '../components/tenant/ProspectPersonSwitcher';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useTenantApiToken, useTenantProfile } from '../contexts/TenantProfileContext';
import {
  exchangeProspectPadPublicToken,
  sendBlankProspectPadAgreement,
  fetchProspectApplicationFile,
  fetchProspectClosingChecklist,
  saveProspectReferral,
  fetchProspectGroup,
  fetchProspectLeaseSigning,
  getProspectApplicationChecks,
  getProspectSelection,
  openProspectApplicationDocument,
  removeProspectClosingDocument,
  uploadProspectClosingFile,
  type ProspectGroupPortalData,
  type ProspectSelection,
} from '../services/tenantApi';
import {
  groupStateFromClosingChecklist,
  personalFromClosingChecklist,
} from '../components/closing/apply-closing-checklist';
import { hasPendingClosingFiles } from '../components/closing/closing-checklist-sync';
import {
  useClosingCardSavePhase,
  type ClosingSaveCardKey,
} from '../hooks/useClosingCardSavePhase';
import { isAutosaveNetworkError } from '../hooks/useAutosave';
import { closingMembersFromChecklistRows } from '../components/closing/closing-roster';
import { mergeClosingPersonalFromApplicationChecks } from '../utils/closing-checklist-merge';
import {
  closingGroupProgress,
  preloadedClosingProgressByPersonId,
} from '../utils/closing-progress';
import { leaseSigningProgress } from '../utils/prospect-sidebar-progress';
import type { ProspectClosingChecklistDto } from '../services/tenantApi';

const CLOSING_PROGRESS_POLL_MS = 30_000;

export default function LeaseSigningPage() {
  const getToken = useTenantApiToken();
  const { prospect } = useTenantProfile();
  const setPageProgress = useSetPageProgress();
  const [searchParams] = useSearchParams();
  const preview = import.meta.env.DEV && searchParams.get('preview') === 'sample';
  const [selection, setSelection] = useState<ProspectSelection | null>(null);
  const [group, setGroup] = useState<ProspectGroupPortalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [personal, setPersonal] = useState<Record<string, ClosingPersonalState>>({});
  const [groupState, setGroupState] = useState<ClosingGroupState>(emptyGroupState);
  const [utilityFile, setUtilityFile] = useState<File | null>(null);
  const [housemateFile, setHousemateFile] = useState<File | null>(null);
  const [padFiles, setPadFiles] = useState<Record<string, File>>({});
  const [voidChequeFiles, setVoidChequeFiles] = useState<Record<string, File>>({});
  const [eTransferFiles, setETransferFiles] = useState<Record<string, File>>({});
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [checklistReady, setChecklistReady] = useState(false);
  const [checklistMemberRows, setChecklistMemberRows] = useState<
    import('../services/tenantApi').ProspectClosingChecklistMemberDto[]
  >([]);
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [referrerName, setReferrerName] = useState(prospect?.referralName ?? '');
  const [referrerSource, setReferrerSource] = useState(prospect?.referralSource ?? '');
  const sampleKey = useRef<string | null>(null);
  const fullLoadVersion = useRef(0);
  const localFilesRef = useRef({
    utilityFile: null as File | null,
    housemateFile: null as File | null,
    padFiles: {} as Record<string, File>,
    voidChequeFiles: {} as Record<string, File>,
    eTransferFiles: {} as Record<string, File>,
  });
  const { phases: closingSavePhases, markSaving, markSaved, markIdle } = useClosingCardSavePhase();

  localFilesRef.current = { utilityFile, housemateFile, padFiles, voidChequeFiles, eTransferFiles };

  const getSavedDocumentUrl = useCallback(
    (documentId: string) => openProspectApplicationDocument(getToken, documentId),
    [getToken],
  );

  const applyServerChecklist = useCallback(
    (dto: ProspectClosingChecklistDto, options?: { resetLocalFiles?: boolean }) => {
      const resetLocalFiles = options?.resetLocalFiles ?? !hasPendingClosingFiles(localFilesRef.current);
      setGroupState((current) => groupStateFromClosingChecklist(dto, current));
      setPersonal(personalFromClosingChecklist(dto));
      setChecklistMemberRows(dto.members ?? []);
      if (resetLocalFiles) {
        setUtilityFile(null);
        setHousemateFile(null);
        setPadFiles({});
        setVoidChequeFiles({});
        setETransferFiles({});
      }
    },
    [],
  );

  const runClosingCard = useCallback(
    async (cardKey: ClosingSaveCardKey, task: () => Promise<ProspectClosingChecklistDto>) => {
      markSaving(cardKey);
      setChecklistError(null);
      try {
        const dto = await task();
        applyServerChecklist(dto, { resetLocalFiles: true });
        fullLoadVersion.current += 1;
        markSaved(cardKey);
      } catch (err) {
        markIdle(cardKey);
        setChecklistError(
          isAutosaveNetworkError(err)
            ? 'We could not reach the server. Try again when your connection is back.'
            : err instanceof Error
              ? err.message
              : 'Unable to save closing step.',
        );
      }
    },
    [applyServerChecklist, markIdle, markSaved, markSaving],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      getProspectSelection(getToken),
      fetchProspectGroup(getToken),
    ])
      .then(([selectionResult, groupResult]) => {
        if (cancelled) return;
        setGroup(groupResult.status === 'fulfilled' ? groupResult.value : null);
        if (selectionResult.status === 'fulfilled') {
          setSelection(selectionResult.value);
          setError(null);
          return;
        }
        if (preview) {
          setSelection(null);
          setError(null);
          return;
        }
        const reason = selectionResult.reason;
        setError(reason instanceof Error ? reason.message : 'Unable to load closing.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, preview]);

  const selected = selection?.homes.find((home) => home.unitId === selection.selectedUnitId) ?? null;
  const showChecklist = Boolean(selected) || preview;
  const viewerPersonId = prospect?.personId ?? null;
  const viewerName = prospect?.displayName?.trim() || 'You';

  const fallbackMembers = useMemo(
    () => membersFromGroup(group, viewerPersonId, viewerName),
    [group, viewerPersonId, viewerName],
  );
  const members = useMemo(() => {
    if (checklistMemberRows.length === 0) return fallbackMembers;
    return closingMembersFromChecklistRows(checklistMemberRows, personal, viewerPersonId);
  }, [checklistMemberRows, fallbackMembers, personal, viewerPersonId]);
  const roster = useMemo(
    () => (preview && showChecklist ? withPreviewRoster(members) : members),
    [preview, showChecklist, members],
  );
  const rosterKey = roster.map((member) => member.id).join('\0');

  useEffect(() => {
    if (!preview || !showChecklist || roster.length === 0) return;
    if (sampleKey.current === rosterKey) return;
    sampleKey.current = rosterKey;
    setPersonal(samplePersonal(roster));
    setGroupState(sampleGroup(roster));
    setUtilityFile(null);
    setHousemateFile(null);
    setPadFiles({});
    setVoidChequeFiles({});
    setETransferFiles({});
  }, [preview, showChecklist, roster, rosterKey]);

  useEffect(() => {
    if (preview || roster.length === 0) return;
    setPersonal((current) => {
      let changed = false;
      const next = { ...current };
      for (const member of roster) {
        if (!next[member.id]) {
          next[member.id] = emptyPersonal();
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [preview, roster]);

  useEffect(() => {
    if (preview || !showChecklist) {
      setChecklistReady(preview);
      return undefined;
    }
    let cancelled = false;
    setChecklistReady(false);
    setChecklistError(null);
    void Promise.allSettled([fetchProspectLeaseSigning(getToken), fetchProspectClosingChecklist(getToken)])
      .then(([leaseResult, checklistResult]) => {
        if (cancelled) return;
        if (checklistResult.status === 'fulfilled') {
          applyServerChecklist(checklistResult.value, { resetLocalFiles: true });
          fullLoadVersion.current += 1;
        } else {
          const reason = checklistResult.reason;
          setChecklistError(reason instanceof Error ? reason.message : 'Unable to load closing checklist.');
        }
        if (leaseResult.status === 'fulfilled') {
          setGroupState((current) => applyLeaseSigningStatus(current, leaseResult.value));
        }
      })
      .finally(() => {
        if (!cancelled) setChecklistReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, showChecklist, getToken, group?.id, applyServerChecklist]);

  useEffect(() => {
    if (preview || !showChecklist || !checklistReady) return undefined;
    let cancelled = false;
    let inFlight = false;
    const pull = () => {
      if (cancelled || inFlight || document.visibilityState === 'hidden' || fullLoadVersion.current === 0) return;
      const version = fullLoadVersion.current;
      inFlight = true;
      void Promise.allSettled([
        fetchProspectLeaseSigning(getToken),
        fetchProspectClosingChecklist(getToken),
        getProspectApplicationChecks(getToken),
      ])
        .then(([leaseResult, checklistResult, checksResult]) => {
          if (cancelled || fullLoadVersion.current !== version) return;
          if (checklistResult.status === 'fulfilled') {
            applyServerChecklist(checklistResult.value, {
              resetLocalFiles: !hasPendingClosingFiles(localFilesRef.current),
            });
          }
          if (checksResult.status === 'fulfilled') {
            setPersonal((current) => mergeClosingPersonalFromApplicationChecks(current, checksResult.value));
          }
          if (leaseResult.status === 'fulfilled') {
            setGroupState((current) => applyLeaseSigningStatus(current, leaseResult.value));
          }
        })
        .finally(() => {
          inFlight = false;
        });
    };
    const interval = window.setInterval(pull, CLOSING_PROGRESS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pull();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [preview, showChecklist, checklistReady, getToken, applyServerChecklist]);

  const rosterWithHeadshots = useMemo(
    () =>
      roster.map((member) => ({
        ...member,
        headshotDocumentId: personal[member.id]?.headshotDocumentId ?? member.headshotDocumentId,
      })),
    [roster, personal],
  );

  const checklist = useMemo<ClosingChecklist>(() => ({
    selected: showChecklist,
    members: rosterWithHeadshots,
    personal,
    group: groupState,
  }), [showChecklist, rosterWithHeadshots, personal, groupState]);

  const preloadedProgressByPersonId = useMemo(
    () => preloadedClosingProgressByPersonId(rosterWithHeadshots, personal),
    [rosterWithHeadshots, personal],
  );

  const progressForPerson = useCallback(
    (personId: string) => preloadedProgressByPersonId[personId] ?? { done: 0, total: 3, percent: 0 },
    [preloadedProgressByPersonId],
  );

  const groupProgress =
    showChecklist && (checklistReady || preview) ? closingGroupProgress(checklist) : null;

  useEffect(() => {
    if (loading || error) return;
    setPageProgress('/lease-signing', leaseSigningProgress(showChecklist ? checklist : null));
  }, [loading, error, showChecklist, checklist, setPageProgress]);

  const groupTasksProgress = useMemo(() => {
    const step = groupStepProgress(rosterWithHeadshots, groupState);
    const percent = step.total <= 0 ? 0 : Math.round((step.completed / step.total) * 100);
    return { done: step.completed, total: step.total, percent };
  }, [rosterWithHeadshots, groupState]);

  const selectedPerson = rosterWithHeadshots.find((member) => member.id === selectedPersonId)
    ?? rosterWithHeadshots.find((member) => member.isViewer)
    ?? rosterWithHeadshots[0]
    ?? null;

  /** Hydrate referral fields once per prospect — avoid profile refetch overwriting in-progress edits. */
  useEffect(() => {
    if (!prospect?.personId) return;
    setReferrerName(prospect.referralName ?? '');
    setReferrerSource(prospect.referralSource ?? '');
  }, [prospect?.personId]);

  const referralSnapshot = JSON.stringify({ referrerName, referrerSource });
  const referralSave = useAutosave({
    snapshot: referralSnapshot,
    ready: Boolean(prospect?.personId) && !preview,
    enabled: Boolean(prospect?.personId) && !preview,
    resetKey: prospect?.personId ?? 'closing-referral',
    save: async (snapshot, context) => {
      const payload = JSON.parse(snapshot) as { referrerName: string; referrerSource: string };
      const saved = await saveProspectReferral(getToken, payload, { keepalive: context.keepalive });
      setReferrerName(saved.referrerName ?? payload.referrerName);
      setReferrerSource(saved.referrerSource ?? payload.referrerSource ?? '');
    },
  });
  const referralCardSave = useCardSaveBadges(
    { referral: referralSnapshot },
    referralSave.status,
    Boolean(prospect?.personId) && !preview,
    prospect?.personId ?? 'closing-referral',
  );

  function updateViewer(patch: Partial<ClosingPersonalState>) {
    if (!selectedPerson?.isViewer) return;
    const personId = selectedPerson.id;
    setPersonal((current) => ({
      ...current,
      [personId]: { ...personalOrEmpty(current, personId), ...patch },
    }));
  }

  return (
    <div className="flex w-full flex-col gap-4 py-2 sm:py-4">
      <header className={CLOSING_CARD}>
        <div className="flex flex-col gap-5 min-[800px]:flex-row min-[800px]:items-start min-[800px]:gap-10">
          <div className="min-w-0 min-[800px]:flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 8</p>
            <h1 className="mt-1 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Sign Your Lease</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base">
              {showChecklist
                ? 'Follow the steps below to finalize your lease with Amber Peak.'
                : 'When your group is selected for a home, the closing steps appear here.'}
            </p>
          </div>
          {groupProgress ? (
            <div className="w-full shrink-0 min-[800px]:w-[min(42rem,56%)]">
              <ProgressTrack label="Your progress" progress={groupProgress} />
            </div>
          ) : null}
        </div>
      </header>

      {preview && showChecklist ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Preview data. Nothing on this page is saved.</p>
      ) : null}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading closing
        </div>
      )}
      {error && <p className="text-sm text-red-700 dark:text-red-400">{error}</p>}
      {checklistError && <p className="text-sm text-red-700 dark:text-red-400">{checklistError}</p>}

      {!loading && !error && selection && !showChecklist && (
        <section className={CLOSING_CARD}>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Your group has not been selected for a unit yet. Information on this page will become available once you are.
          </p>
        </section>
      )}

      {!loading && !error && showChecklist && !checklistReady && !preview && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading closing checklist
        </div>
      )}

      {!loading && !error && showChecklist && (checklistReady || preview) && selectedPerson && (
        <>
          <GroupTasksMarker progress={groupTasksProgress} />
          <ClosingGroupCards
            members={rosterWithHeadshots}
            group={groupState}
            utilitySavePhase={closingSavePhases.utilities}
            housemateSavePhase={closingSavePhases.housemate}
            getSavedDocumentUrl={preview ? undefined : getSavedDocumentUrl}
            onDownloadLease={preview ? undefined : async (documentId, fileName) => {
              const blob = await fetchProspectApplicationFile(getToken, documentId);
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName || 'lease-agreement.pdf';
              document.body.appendChild(link);
              link.click();
              link.remove();
              URL.revokeObjectURL(url);
            }}
            utilityFile={utilityFile}
            housemateFile={housemateFile}
            onUtilityFile={(file) => {
              setUtilityFile(file);
              void runClosingCard('utilities', () => uploadProspectClosingFile(getToken, file, 'UTILITY_SETUP_CONFIRMATION'));
            }}
            onClearUtility={() => {
              setUtilityFile(null);
            }}
            onDeleteUtility={
              preview || !groupState.utilityDocumentId
                ? undefined
                : () => void runClosingCard('utilities', () => removeProspectClosingDocument(getToken, 'UTILITY_SETUP_CONFIRMATION'))
            }
            onHousemateFile={(file) => {
              setHousemateFile(file);
              void runClosingCard('housemate', () => uploadProspectClosingFile(getToken, file, 'HOUSEMATE_AGREEMENT'));
            }}
            onClearHousemate={() => {
              setHousemateFile(null);
            }}
            onDeleteHousemate={
              preview || !groupState.housemateDocumentId
                ? undefined
                : () => void runClosingCard('housemate', () => removeProspectClosingDocument(getToken, 'HOUSEMATE_AGREEMENT'))
            }
            referral={
              selectedPerson?.isViewer
                ? {
                    referrerName,
                    referrerSource,
                    onReferrerNameChange: setReferrerName,
                    onReferrerSourceChange: setReferrerSource,
                    savePhase: preview ? undefined : referralCardSave.referral,
                    nameId: 'closing-referral-name',
                    sourceId: 'closing-referral-source',
                  }
                : undefined
            }
          />
          <ProspectPersonSwitcher
            people={rosterWithHeadshots.map((member) => ({
              id: member.id,
              displayName: member.displayName,
              isViewer: member.isViewer,
              headshotDocumentId: member.headshotDocumentId,
            }))}
            selectedId={selectedPerson.id}
            progressForPerson={progressForPerson}
            onSelect={setSelectedPersonId}
          />
          <ClosingPersonSteps
            person={selectedPerson}
            propertyLabel={selected?.label ?? ''}
            state={personalOrEmpty(personal, selectedPerson.id)}
            padSavePhase={closingSavePhases.pad}
            voidChequeSavePhase={closingSavePhases.voidCheque}
            eTransferSavePhase={closingSavePhases.eTransfer}
            getSavedDocumentUrl={preview ? undefined : getSavedDocumentUrl}
            padDocumentId={personalOrEmpty(personal, selectedPerson.id).padDocumentId}
            voidChequeDocumentId={personalOrEmpty(personal, selectedPerson.id).voidChequeDocumentId}
            padFile={padFiles[selectedPerson.id] ?? null}
            voidChequeFile={voidChequeFiles[selectedPerson.id] ?? null}
            eTransferFile={eTransferFiles[selectedPerson.id] ?? null}
            eTransferDocumentId={personalOrEmpty(personal, selectedPerson.id).lastMonthRentETransferDocumentId}
            onPadFile={(file) => {
              const personId = selectedPerson.id;
              setPadFiles((current) => ({ ...current, [personId]: file }));
              if (selectedPerson.isViewer) {
                void runClosingCard('pad', () => uploadProspectClosingFile(getToken, file, 'PAD'));
              }
            }}
            onClearPad={() => {
              const personId = selectedPerson.id;
              setPadFiles((current) => {
                const next = { ...current };
                delete next[personId];
                return next;
              });
            }}
            onDeletePad={
              preview || !selectedPerson.isViewer || !personalOrEmpty(personal, selectedPerson.id).padDocumentId
                ? undefined
                : () => void runClosingCard('pad', () => removeProspectClosingDocument(getToken, 'PAD'))
            }
            onReplacePad={
              preview || !selectedPerson.isViewer || !personalOrEmpty(personal, selectedPerson.id).padDocumentId
                ? undefined
                : () => void runClosingCard('pad', () => removeProspectClosingDocument(getToken, 'PAD'))
            }
            padPlaid={
              preview || !selectedPerson.isViewer ? undefined : (
                <ClosingPadPlaidButton
                  getToken={getToken}
                  onExchange={(publicToken) => runClosingCard('pad', () => exchangeProspectPadPublicToken(getToken, publicToken))}
                />
              )
            }
            onSendBlankPad={
              preview || !selectedPerson.isViewer
                ? undefined
                : () => runClosingCard('pad', () => sendBlankProspectPadAgreement(getToken))
            }
            onVoidChequeFile={(file) => {
              const personId = selectedPerson.id;
              setVoidChequeFiles((current) => ({ ...current, [personId]: file }));
              if (selectedPerson.isViewer) {
                void runClosingCard('voidCheque', () => uploadProspectClosingFile(getToken, file, 'VOID_CHEQUE'));
              }
            }}
            onClearVoidCheque={() => {
              const personId = selectedPerson.id;
              setVoidChequeFiles((current) => {
                const next = { ...current };
                delete next[personId];
                return next;
              });
            }}
            onDeleteVoidCheque={
              preview || !selectedPerson.isViewer || !personalOrEmpty(personal, selectedPerson.id).voidChequeDocumentId
                ? undefined
                : () => void runClosingCard('voidCheque', () => removeProspectClosingDocument(getToken, 'VOID_CHEQUE'))
            }
            onETransferFile={(file) => {
              const personId = selectedPerson.id;
              setETransferFiles((current) => ({ ...current, [personId]: file }));
              if (selectedPerson.isViewer) {
                void runClosingCard('eTransfer', () =>
                  uploadProspectClosingFile(getToken, file, 'LAST_MONTH_RENT_E_TRANSFER_SENT'),
                );
              }
            }}
            onClearETransfer={() => {
              const personId = selectedPerson.id;
              setETransferFiles((current) => {
                const next = { ...current };
                delete next[personId];
                return next;
              });
            }}
            onDeleteETransfer={
              preview ||
              !selectedPerson.isViewer ||
              !personalOrEmpty(personal, selectedPerson.id).lastMonthRentETransferDocumentId
                ? undefined
                : () =>
                    void runClosingCard('eTransfer', () =>
                      removeProspectClosingDocument(getToken, 'LAST_MONTH_RENT_E_TRANSFER_SENT'),
                    )
            }
          />
        </>
      )}
    </div>
  );
}
